import React, { useState, useEffect, useRef } from 'react';
import {
  Typography, Paper, TextField, Grid, Button, MenuItem,
  FormHelperText, Stepper, Step, StepLabel, Box, Container,
  Card, CardContent, Checkbox, Divider
} from '@mui/material';
import { ArrowBack, ArrowForward, Save, CheckCircle, Close } from '@mui/icons-material';
import { Link, useSearchParams } from 'react-router-dom';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import axios from '../utils/api';
import PhotoUpload from './PhotoUpload';
import { uploadPhotoForLawyer } from '../utils/photoUtils';
import { US_STATES } from '../utils/constants';
import ReferralOnlyRegistrationForm from './ReferralOnlyRegistrationForm';
import LawyerTermsCheckbox from './LawyerTermsCheckbox';

const phoneTypes = [
  { value: 'office', label: 'Work' },
  { value: 'cell', label: 'Cellphone' },
  { value: 'other', label: 'Other' },
];

interface ValidationErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  firmName?: string;
  yearsPracticingLaw?: string;
  lawSchool?: string;
  lawSchoolGraduationYear?: string;
  phoneNumbers?: string;
  primaryOffice?: {
    line1?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  stateLicenses?: string;
  practiceAreas?: string;
  practiceAreaPercentages?: string;
  discountedHourlyRate?: string;
  disciplinary?: string;
  disciplinaryNarrative?: string;
  proBono?: string;
  offersFreeConsultation?: string;
  hasProfessionalLiabilityInsurance?: string;
}

interface LawyerRegistrationFormProps {
  registrationType: 'directory' | 'both';
  onBackToChoice: () => void;
}

const LawyerRegistrationForm: React.FC<LawyerRegistrationFormProps> = ({ registrationType, onBackToChoice }) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [activeStep, setActiveStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [confirmationNumber, setConfirmationNumber] = useState('');
  const [registrationEmail, setRegistrationEmail] = useState('');
  const [resendingVerification, setResendingVerification] = useState(false);
  const [resendMessage, setResendMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  
  const [form, setForm] = useState({
    firstName: '',
    middleInitial: '',
    lastName: '',
    suffix: '',
    email: '',
    firmName: '',
    practiceWebsiteUrl: '',
    linkedInUrl: '',
    yearsPracticingLaw: '',
    lawSchool: '',
    lawSchoolGraduationYear: '',
    comments: '',
  });
  const [phoneNumbers, setPhoneNumbers] = useState([
    { number: '', type: 'office' },
    { number: '', type: 'cell' },
  ]);
  const [primaryOffice, setPrimaryOffice] = useState({
    line1: '',
    line2: '',
    city: '',
    state: '',
    zipCode: '',
  });
  const [otherOffices, setOtherOffices] = useState('');
  const [stateLicenses, setStateLicenses] = useState([
    { state: '', barIdNo: '', isActive: '', inGoodStanding: '', statusNote: '' }
  ]);
  const [practiceAreaCategories, setPracticeAreaCategories] = useState<Array<{
    id: string;
    name: string;
    hasSubcategories: boolean;
    subcategories: Array<{ id: string; name: string; categoryId: string; requiresCustomText?: boolean; customTextRequired?: boolean }>;
  }>>([]);
  const [practiceAreas, setPracticeAreas] = useState<Array<{
    categoryId: string;
    categoryName: string;
    subcategoryId?: string | null;
    subcategoryName?: string;
    otherArea?: string;
    subCategoryCustomText?: string;
    percentage: number;
  }>>([]);
  const [loadingPracticeAreas, setLoadingPracticeAreas] = useState(true);
  const [selectedCategoryForAdd, setSelectedCategoryForAdd] = useState<string>('');
  // Track which subcategory checkboxes are checked
  const [checkedSubcategories, setCheckedSubcategories] = useState<Set<string>>(new Set());
  // Track percentage values for subcategories (even when not in list)
  const [subcategoryPercentages, setSubcategoryPercentages] = useState<Record<string, number>>({});
  // Track validation errors for individual subcategory percentages
  const [subcategoryPercentageErrors, setSubcategoryPercentageErrors] = useState<Record<string, string>>({});
  // Track custom text for subcategories that require it
  const [subcategoryCustomTexts, setSubcategoryCustomTexts] = useState<Record<string, string>>({});
  // Track validation errors for subcategory custom text
  const [subcategoryCustomTextErrors, setSubcategoryCustomTextErrors] = useState<Record<string, string>>({});
  // Refs for custom text fields to enable scrolling
  const customTextFieldRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const scrolledCustomTextKeysRef = useRef(new Set<string>());
  const pendingOneTimeScrollKeyRef = useRef<string | null>(null);
  const pendingSubmitCustomTextScrollKeyRef = useRef<string | null>(null);
  // Track "Other" category entries (multiple allowed)
  const [otherCategoryRows, setOtherCategoryRows] = useState<{ text: string; percentage: string; error: string }[]>([{ text: '', percentage: '', error: '' }]);
  const [disciplinary, setDisciplinary] = useState<{ hasAction: '' | 'yes' | 'no'; narrative: string }>({ hasAction: '', narrative: '' });
  const [feeInfo, setFeeInfo] = useState({ hourlyRate: '', discountedHourlyRate: '', otherFees: '' });
  const [proBonoInterest, setProBonoInterest] = useState<'' | 'yes' | 'no'>('');
  const [offersFreeConsultation, setOffersFreeConsultation] = useState<'' | 'yes' | 'no'>('');
  const [hasProfessionalLiabilityInsurance, setHasProfessionalLiabilityInsurance] = useState<'' | 'yes' | 'no'>('');
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);
  const [profilePhotoFile, setProfilePhotoFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string|null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [termsAccepted, setTermsAccepted] = useState(false);

  const steps = ['Basic Information', 'Office & Licenses', 'Practice & Details'];

  // Fetch practice area categories on mount
  useEffect(() => {
    const fetchPracticeAreas = async () => {
      try {
        const response = await axios.get('/api/practice-areas');
        setPracticeAreaCategories(response.data);
        setLoadingPracticeAreas(false);
      } catch (error) {
        console.error('Error fetching practice areas:', error);
        setLoadingPracticeAreas(false);
      }
    };
    fetchPracticeAreas();
  }, []);

  useEffect(() => {
    const key = pendingOneTimeScrollKeyRef.current;
    if (!key || scrolledCustomTextKeysRef.current.has(key)) return;

    const el = customTextFieldRefs.current[key];
    if (!el) return;

    scrolledCustomTextKeysRef.current.add(key);
    pendingOneTimeScrollKeyRef.current = null;
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    const input = el.querySelector('input, textarea');
    if (input instanceof HTMLElement) {
      input.focus({ preventScroll: true });
    }
  }, [checkedSubcategories, selectedCategoryForAdd]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhoneNumber = (phone: string) => {
    // Remove all non-digit characters for validation
    const cleaned = phone.replace(/\D/g, '');
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(cleaned);
  };

  const validateZipCode = (zip: string) => {
    const zipRegex = /^\d{5}(-\d{4})?$/;
    return zipRegex.test(zip);
  };

  // Function to format phone number as (xxx) xxx-xxxx
  const formatPhoneNumber = (phoneNumber: string): string => {
    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // If it's a 10-digit number, format it
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    
    // If it's 11 digits and starts with 1, format it
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    
    // Return original if it doesn't match expected format
    return phoneNumber;
  };

  // Function to handle phone number formatting on blur
  const handlePhoneBlur = (idx: number) => {
    const phone = phoneNumbers[idx];
    if (phone.number.trim()) {
      const formatted = formatPhoneNumber(phone.number);
      setPhoneNumbers(prev => prev.map((p, i) => i === idx ? { ...p, number: formatted } : p));
    }
  };

  const handleEnterAsBlur = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (e.target instanceof HTMLElement) {
        e.target.blur();
      }
    }
  };

  const scrollToCustomTextFieldWithRetry = (key: string, onComplete?: () => void) => {
    const attempt = (n: number) => {
      const el = customTextFieldRefs.current[key];
      if (el) {
        const yOffset = -100;
        const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
        const input = el.querySelector('input, textarea');
        if (input instanceof HTMLElement) {
          setTimeout(() => input.focus({ preventScroll: true }), 300);
        }
        onComplete?.();
        return;
      }
      if (n < 10) {
        setTimeout(() => attempt(n + 1), 100);
      }
    };
    attempt(0);
  };

  const scrollToFirstError = (errors: ValidationErrors) => {
    // Use setTimeout to ensure DOM has updated with error states
    setTimeout(() => {
      let fieldToScroll: HTMLElement | null = null;

      if (activeStep === 0) {
        // Step 0: Basic Information
        if (errors.firstName) {
          // Find the TextField container for firstName
          const input = document.querySelector('input[name="firstName"]') as HTMLInputElement;
          fieldToScroll = input?.closest('.MuiTextField-root') as HTMLElement || input;
        } else if (errors.lastName) {
          const input = document.querySelector('input[name="lastName"]') as HTMLInputElement;
          fieldToScroll = input?.closest('.MuiTextField-root') as HTMLElement || input;
        } else if (errors.email) {
          const input = document.querySelector('input[name="email"]') as HTMLInputElement;
          fieldToScroll = input?.closest('.MuiTextField-root') as HTMLElement || input;
        } else if (errors.firmName) {
          const input = document.querySelector('input[name="firmName"]') as HTMLInputElement;
          fieldToScroll = input?.closest('.MuiTextField-root') as HTMLElement || input;
        } else if (errors.phoneNumbers) {
          // Scroll to first phone number field
          const input = document.querySelector('input[type="tel"]') as HTMLInputElement;
          fieldToScroll = input?.closest('.MuiTextField-root') as HTMLElement || input;
        } else if (errors.yearsPracticingLaw) {
          const input = document.querySelector('input[name="yearsPracticingLaw"]') as HTMLInputElement;
          fieldToScroll = input?.closest('.MuiTextField-root') as HTMLElement || input;
        } else if (errors.lawSchool) {
          const input = document.querySelector('input[name="lawSchool"]') as HTMLInputElement;
          fieldToScroll = input?.closest('.MuiTextField-root') as HTMLElement || input;
        } else if (errors.lawSchoolGraduationYear) {
          const input = document.querySelector('input[name="lawSchoolGraduationYear"]') as HTMLInputElement;
          fieldToScroll = input?.closest('.MuiTextField-root') as HTMLElement || input;
        }
      } else if (activeStep === 1) {
        // Step 1: Office & Licenses
        if (errors.primaryOffice) {
          if (errors.primaryOffice.line1) {
            const input = document.querySelector('input[name="line1"]') as HTMLInputElement;
            fieldToScroll = input?.closest('.MuiTextField-root') as HTMLElement || input;
          } else if (errors.primaryOffice.city) {
            const input = document.querySelector('input[name="city"]') as HTMLInputElement;
            fieldToScroll = input?.closest('.MuiTextField-root') as HTMLElement || input;
          } else if (errors.primaryOffice.state) {
            // State is a select field
            const selects = document.querySelectorAll('div[role="combobox"]');
            // Find the one that's for primary office state (usually the first one)
            for (const select of Array.from(selects)) {
              const label = select.closest('.MuiTextField-root')?.querySelector('label');
              if (label?.textContent?.includes('State') && !label.textContent.includes('License')) {
                fieldToScroll = select as HTMLElement;
                break;
              }
            }
            if (!fieldToScroll && selects.length > 0) {
              fieldToScroll = selects[0] as HTMLElement;
            }
          } else if (errors.primaryOffice.zipCode) {
            const input = document.querySelector('input[name="zipCode"]') as HTMLInputElement;
            fieldToScroll = input?.closest('.MuiTextField-root') as HTMLElement || input;
          }
        } else if (errors.stateLicenses) {
          // Scroll to first state license state field
          const selects = document.querySelectorAll('div[role="combobox"]');
          // Find the one for state license (usually has "State" label and is in state license section)
          for (const select of Array.from(selects)) {
            const label = select.closest('.MuiTextField-root')?.querySelector('label');
            if (label?.textContent === 'State') {
              // Check if it's in the state licenses section (not primary office)
              const gridItem = select.closest('[class*="MuiGrid-item"]');
              if (gridItem) {
                fieldToScroll = select as HTMLElement;
                break;
              }
            }
          }
          // Fallback: find first barIdNo input
          if (!fieldToScroll) {
            const input = document.querySelector('input[name="barIdNo"]') as HTMLInputElement;
            fieldToScroll = input?.closest('.MuiTextField-root') as HTMLElement || input;
          }
        } else if (errors.hasProfessionalLiabilityInsurance) {
          const input = document.querySelector('input[name="hasProfessionalLiabilityInsurance"]') as HTMLInputElement;
          fieldToScroll = input?.closest('.MuiTextField-root') as HTMLElement || input;
        }
      } else if (activeStep === 2) {
        // Step 2: Practice & Details
        if (errors.practiceAreas && pendingSubmitCustomTextScrollKeyRef.current) {
          const key = pendingSubmitCustomTextScrollKeyRef.current;
          scrollToCustomTextFieldWithRetry(key, () => {
            pendingSubmitCustomTextScrollKeyRef.current = null;
          });
          return;
        }
        if (errors.practiceAreas || errors.practiceAreaPercentages) {
          // Try to find the category selector first
          const categorySelect = document.querySelector('div[role="combobox"]');
          if (categorySelect) {
            const label = categorySelect.closest('.MuiTextField-root')?.querySelector('label');
            if (label?.textContent === 'Category') {
              fieldToScroll = categorySelect as HTMLElement;
            }
          }
          // If not found, try to find first percentage input
          if (!fieldToScroll) {
            const percentageInputs = document.querySelectorAll('input[type="number"]');
            for (const input of Array.from(percentageInputs)) {
              const label = input.closest('.MuiTextField-root')?.querySelector('label');
              if (label?.textContent?.includes('Percentage')) {
                fieldToScroll = input as HTMLElement;
                break;
              }
            }
          }
        }
        if (!fieldToScroll && errors.disciplinary) {
          const anchor = document.getElementById('reg-step2-disciplinary-select');
          fieldToScroll =
            (anchor?.querySelector('div[role="combobox"]') as HTMLElement) ||
            (anchor?.querySelector('.MuiTextField-root') as HTMLElement) ||
            anchor;
        }
        if (!fieldToScroll && errors.disciplinaryNarrative) {
          const anchor = document.getElementById('reg-step2-disciplinary-narrative');
          fieldToScroll =
            (anchor?.querySelector('textarea') as HTMLElement) ||
            (anchor?.querySelector('.MuiTextField-root') as HTMLElement) ||
            anchor;
        }
        if (!fieldToScroll && errors.proBono) {
          const anchor = document.getElementById('reg-step2-pro-bono');
          fieldToScroll =
            (anchor?.querySelector('div[role="combobox"]') as HTMLElement) ||
            (anchor?.querySelector('.MuiTextField-root') as HTMLElement) ||
            anchor;
        }
        if (!fieldToScroll && errors.offersFreeConsultation) {
          const anchor = document.getElementById('reg-step3-free-consultation');
          fieldToScroll =
            (anchor?.querySelector('div[role="combobox"]') as HTMLElement) ||
            (anchor?.querySelector('.MuiTextField-root') as HTMLElement) ||
            anchor;
        }
      }

      if (fieldToScroll) {
        // Scroll to the field with some offset from top
        const yOffset = -100; // Offset to account for fixed headers
        const y = fieldToScroll.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
        
        // Focus the field if it's an input
        if (fieldToScroll instanceof HTMLInputElement || fieldToScroll instanceof HTMLTextAreaElement) {
          setTimeout(() => {
            fieldToScroll?.focus();
          }, 300);
        } else if (fieldToScroll instanceof HTMLElement) {
          // For select fields, try to find and focus the input inside
          const input = fieldToScroll.querySelector('input');
          if (input) {
            setTimeout(() => {
              input.focus();
            }, 300);
          }
        }
      }
    }, 100);
  };

  const validateCurrentStep = (): boolean => {
    const errors: ValidationErrors = {};

    if (activeStep === 0) {
      // Page 1 validation
      if (!form.firstName.trim()) errors.firstName = 'First name is required';
      if (!form.lastName.trim()) errors.lastName = 'Last name is required';
      if (!form.email.trim()) {
        errors.email = 'Email is required';
      } else if (!validateEmail(form.email)) {
        errors.email = 'Please enter a valid email address';
      }
      if (!form.firmName.trim()) errors.firmName = 'Firm name is required';
      if (!form.yearsPracticingLaw.trim()) {
        errors.yearsPracticingLaw = 'Years of practice is required';
      } else if (!/^\d+$/.test(form.yearsPracticingLaw) || Number(form.yearsPracticingLaw) < 0 || Number(form.yearsPracticingLaw) > 80) {
        errors.yearsPracticingLaw = 'Enter a whole number between 0 and 80';
      }
      if (!form.lawSchool.trim()) errors.lawSchool = 'Law school is required';
      if (!form.lawSchoolGraduationYear.trim()) {
        errors.lawSchoolGraduationYear = 'Graduation year is required';
      } else if (!/^\d{4}$/.test(form.lawSchoolGraduationYear)) {
        errors.lawSchoolGraduationYear = 'Enter a valid 4-digit year';
      } else {
        const year = Number(form.lawSchoolGraduationYear);
        const maxYear = new Date().getFullYear() + 1;
        if (year < 1950 || year > maxYear) {
          errors.lawSchoolGraduationYear = `Enter a year between 1950 and ${maxYear}`;
        }
      }

      if (!phoneNumbers[0].number.trim()) {
        errors.phoneNumbers = 'Primary phone number is required';
      } else if (!validatePhoneNumber(phoneNumbers[0].number)) {
        errors.phoneNumbers = 'Please enter a valid primary phone number';
      } else if (phoneNumbers[1]?.number.trim() && !validatePhoneNumber(phoneNumbers[1].number)) {
        errors.phoneNumbers = 'Please enter a valid secondary phone number';
      }
    } else if (activeStep === 1) {
      // Page 2 validation
      if (!primaryOffice.line1.trim()) errors.primaryOffice = { ...errors.primaryOffice, line1: 'Address line 1 is required' };
      if (!primaryOffice.city.trim()) errors.primaryOffice = { ...errors.primaryOffice, city: 'City is required' };
      if (!primaryOffice.state.trim()) errors.primaryOffice = { ...errors.primaryOffice, state: 'State is required' };
      if (!primaryOffice.zipCode.trim()) {
        errors.primaryOffice = { ...errors.primaryOffice, zipCode: 'Zip code is required' };
      } else if (!validateZipCode(primaryOffice.zipCode)) {
        errors.primaryOffice = { ...errors.primaryOffice, zipCode: 'Please enter a valid zip code' };
      }

      const validLicenses = stateLicenses.filter(
        l => l.state && l.barIdNo && (l.isActive === 'yes' || l.isActive === 'no') && (l.inGoodStanding === 'yes' || l.inGoodStanding === 'no')
      );
      const hasIncompleteLicenseRow = stateLicenses.some(l => {
        const hasAnyValue = Boolean(l.state || l.barIdNo || l.isActive || l.inGoodStanding || l.statusNote);
        const isComplete =
          Boolean(l.state) &&
          Boolean(l.barIdNo) &&
          (l.isActive === 'yes' || l.isActive === 'no') &&
          (l.inGoodStanding === 'yes' || l.inGoodStanding === 'no');
        return hasAnyValue && !isComplete;
      });
      if (validLicenses.length === 0) {
        errors.stateLicenses = 'At least one complete state license is required';
      } else if (hasIncompleteLicenseRow) {
        errors.stateLicenses = 'For each state license entered, State, Bar ID, Active, and Good Standing are required';
      }

      if (hasProfessionalLiabilityInsurance !== 'yes' && hasProfessionalLiabilityInsurance !== 'no') {
        errors.hasProfessionalLiabilityInsurance = 'Please select Yes or No';
      }
    } else if (activeStep === 2) {
      // Page 3 validation
      if (practiceAreas.length === 0) {
        errors.practiceAreas = 'At least one practice area is required';
      } else {
        // Validate that "Other" category entries have otherArea text
        const hasInvalidOther = practiceAreas.some(pa => 
          pa.otherArea && (!pa.otherArea.trim() || !pa.categoryId)
        );
        
        if (hasInvalidOther) {
          errors.practiceAreas = 'Custom practice area name is required for "Other" category';
        }
        
        // Validate required subcategory custom text
        const invalidSubcategoryCustomText = practiceAreas.filter(pa => {
          if (!pa.subcategoryId) return false;
          const subcategory = practiceAreaCategories
            .flatMap(cat => cat.subcategories)
            .find(sub => sub.id === pa.subcategoryId);
          if (subcategory?.customTextRequired && (!pa.subCategoryCustomText || !pa.subCategoryCustomText.trim())) {
            return true;
          }
          return false;
        });
        
        if (invalidSubcategoryCustomText.length > 0) {
          // Set errors for each invalid subcategory
          invalidSubcategoryCustomText.forEach(pa => {
            const subcategoryKey = `${pa.categoryId}-${pa.subcategoryId}`;
            setSubcategoryCustomTextErrors(prev => ({
              ...prev,
              [subcategoryKey]: 'This field is required'
            }));
          });
          errors.practiceAreas = 'Additional details are required for some selected subcategories';
          const firstInvalid = invalidSubcategoryCustomText[0];
          const scrollKey = `${firstInvalid.categoryId}-${firstInvalid.subcategoryId}`;
          pendingSubmitCustomTextScrollKeyRef.current = scrollKey;
          setSelectedCategoryForAdd(firstInvalid.categoryId);
          setCheckedSubcategories(prev => new Set(prev).add(scrollKey));
        }
        
        // Validate percentages
        const totalPercentage = practiceAreas.reduce((sum, pa) => sum + (pa.percentage || 0), 0);
        const hasInvalidPercentage = practiceAreas.some(pa => 
          !pa.percentage || pa.percentage < 1 || pa.percentage > 100
        );
        
        if (hasInvalidPercentage) {
          errors.practiceAreaPercentages = 'Each practice area must have a percentage between 1% and 100%';
        } else if (totalPercentage !== 100) {
          errors.practiceAreaPercentages = `Percentages must total 100%. Current total: ${totalPercentage}%`;
        }
      }

      if (disciplinary.hasAction !== 'yes' && disciplinary.hasAction !== 'no') {
        errors.disciplinary = 'Please select Yes or No';
      }
      if (disciplinary.hasAction === 'yes' && !disciplinary.narrative.trim()) {
        errors.disciplinaryNarrative = 'Please provide details for your response';
      }
      if (proBonoInterest !== 'yes' && proBonoInterest !== 'no') {
        errors.proBono = 'Please select Yes or No';
      }
      if (offersFreeConsultation !== 'yes' && offersFreeConsultation !== 'no') {
        errors.offersFreeConsultation = 'Please select Yes or No';
      }

      // Validate discounted rate is less than standard rate (only when both are entered)
      if (feeInfo.hourlyRate && feeInfo.discountedHourlyRate) {
        const standard = parseFloat(feeInfo.hourlyRate);
        const discounted = parseFloat(feeInfo.discountedHourlyRate);
        if (!isNaN(standard) && !isNaN(discounted) && discounted >= standard) {
          errors.discountedHourlyRate = 'Discounted rate must be less than the standard hourly rate';
        }
      }
    }

    setValidationErrors(errors);
    
    // If there are errors, scroll to the first error field
    if (Object.keys(errors).length > 0) {
      scrollToFirstError(errors);
    }
    
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      setValidationErrors({});
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      onBackToChoice();
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
      setValidationErrors({});
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (validationErrors[e.target.name as keyof ValidationErrors]) {
      setValidationErrors({ ...validationErrors, [e.target.name]: undefined });
    }
  };

  const handlePhoneChange = (idx: number, field: string, value: string) => {
    setPhoneNumbers(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
    if (validationErrors.phoneNumbers) {
      setValidationErrors({ ...validationErrors, phoneNumbers: undefined });
    }
  };

  const handlePrimaryOfficeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPrimaryOffice({ ...primaryOffice, [e.target.name]: e.target.value });
    if (validationErrors.primaryOffice?.[e.target.name as keyof typeof validationErrors.primaryOffice]) {
      setValidationErrors({
        ...validationErrors,
        primaryOffice: {
          ...validationErrors.primaryOffice,
          [e.target.name]: undefined
        }
      });
    }
  };

  const handleStateLicenseChange = (idx: number, field: string, value: any) => {
    setStateLicenses(prev => prev.map((lic, i) => i === idx ? { ...lic, [field]: value } : lic));
    if (validationErrors.stateLicenses) {
      setValidationErrors({ ...validationErrors, stateLicenses: undefined });
    }
  };

  const addStateLicense = () => {
    setStateLicenses(prev => ([...prev, { state: '', barIdNo: '', isActive: '', inGoodStanding: '', statusNote: '' }]));
  };

  const removeStateLicense = (idx: number) => {
    setStateLicenses(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) {
      return;
    }

    // Get reCAPTCHA token
    if (!executeRecaptcha) {
      setSubmitError('reCAPTCHA not loaded. Please refresh the page and try again.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    
    try {
      // Execute reCAPTCHA with action 'submit'
      const recaptchaToken = await executeRecaptcha('submit');
    
    const payload = {
      ...form,
      yearsPracticingLaw: form.yearsPracticingLaw ? parseInt(form.yearsPracticingLaw, 10) : null,
      lawSchoolGraduationYear: form.lawSchoolGraduationYear ? parseInt(form.lawSchoolGraduationYear, 10) : null,
      profilePhotoUrl: null, // Don't send photo URL yet, will upload after lawyer creation
      phoneNumbers: phoneNumbers.filter(p => p.number),
      primaryOffice: primaryOffice,
      otherOffices,
      stateLicenses: stateLicenses
        .filter(
          l => l.state && l.barIdNo && (l.isActive === 'yes' || l.isActive === 'no') && (l.inGoodStanding === 'yes' || l.inGoodStanding === 'no')
        )
        .map(l => ({
          ...l,
          isActive: l.isActive === 'yes',
          inGoodStanding: l.inGoodStanding === 'yes',
        })),
        practiceAreas: practiceAreas.map(pa => ({
          categoryId: pa.categoryId,
          subcategoryId: pa.subcategoryId || null,
          otherArea: pa.otherArea || undefined,
          subCategoryCustomText: pa.subCategoryCustomText || undefined,
          percentage: pa.percentage
        })),
      disciplinary: [{
        hasAction: disciplinary.hasAction === 'yes',
        narrative: disciplinary.hasAction === 'yes' ? disciplinary.narrative.trim() : '',
      }],
      feeInfo: {
        hourlyRate: feeInfo.hourlyRate ? parseFloat(feeInfo.hourlyRate) : undefined,
        discountedHourlyRate: feeInfo.discountedHourlyRate ? parseFloat(feeInfo.discountedHourlyRate) : undefined,
        otherFees: feeInfo.otherFees,
      },
      proBonoInterest: proBonoInterest === 'yes',
      offersFreeConsultation: offersFreeConsultation === 'yes',
      hasProfessionalLiabilityInsurance: hasProfessionalLiabilityInsurance === 'yes',
        recaptchaToken: recaptchaToken,
    };
    
      const response = await axios.post('/api/lawyers/register', payload);
      const createdLawyer = response.data;
      
      // Upload photo if one was selected
      if (profilePhotoFile && createdLawyer.id) {
        try {
          const photoResult = await uploadPhotoForLawyer(createdLawyer.id, profilePhotoFile);
          if (!photoResult.success) {
            console.warn('Photo upload failed:', photoResult.error);
            // Don't fail the whole operation for photo upload failure
          }
        } catch (photoError) {
          console.warn('Photo upload error:', photoError);
          // Don't fail the whole operation for photo upload failure
        }
      }
      
      setConfirmationNumber(createdLawyer.id);
      setRegistrationEmail(form.email);
      setSubmitted(true);
      setSubmitError(null);
    } catch (err: any) {
      setSubmitError(err?.response?.data?.error || 'Failed to submit registration.');
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={2}>
            {/* Name and Firm Fields */}
            <Grid item xs={12} sm={4}>
              <TextField 
                label="First Name" 
                name="firstName" 
                value={form.firstName} 
                onChange={handleChange} 
                fullWidth 
                required 
                error={!!validationErrors.firstName}
                helperText={validationErrors.firstName}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField 
                label="Middle Initial" 
                name="middleInitial" 
                value={form.middleInitial} 
                onChange={handleChange} 
                fullWidth 
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField 
                label="Last Name" 
                name="lastName" 
                value={form.lastName} 
                onChange={handleChange} 
                fullWidth 
                required 
                error={!!validationErrors.lastName}
                helperText={validationErrors.lastName}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField 
                label="Suffix" 
                name="suffix" 
                value={form.suffix} 
                onChange={handleChange} 
                fullWidth 
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Email" 
                name="email" 
                value={form.email} 
                onChange={handleChange} 
                type="email" 
                fullWidth 
                required 
                error={!!validationErrors.email}
                helperText={validationErrors.email}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Firm Name" 
                name="firmName" 
                value={form.firmName} 
                onChange={handleChange} 
                fullWidth 
                required 
                error={!!validationErrors.firmName}
                helperText={validationErrors.firmName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Business website URL"
                name="practiceWebsiteUrl"
                value={form.practiceWebsiteUrl}
                onChange={handleChange}
                fullWidth
                placeholder="https://www.yourfirm.com"
                helperText="Optional - Your practice website URL"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="LinkedIn URL" 
                name="linkedInUrl" 
                value={form.linkedInUrl} 
                onChange={handleChange} 
                fullWidth 
                placeholder="https://linkedin.com/in/yourprofile"
                helperText="Optional - Your LinkedIn profile URL"
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                label="Years practicing"
                name="yearsPracticingLaw"
                value={form.yearsPracticingLaw}
                onChange={handleChange}
                fullWidth
                required
                type="number"
                inputProps={{ min: 0, max: 80, step: 1 }}
                error={!!validationErrors.yearsPracticingLaw}
                helperText={validationErrors.yearsPracticingLaw}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Law School"
                name="lawSchool"
                value={form.lawSchool}
                onChange={handleChange}
                fullWidth
                required
                error={!!validationErrors.lawSchool}
                helperText={validationErrors.lawSchool}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Law School Graduation Year"
                name="lawSchoolGraduationYear"
                value={form.lawSchoolGraduationYear}
                onChange={handleChange}
                fullWidth
                required
                type="number"
                inputProps={{ min: 1950, max: new Date().getFullYear() + 1, step: 1 }}
                error={!!validationErrors.lawSchoolGraduationYear}
                helperText={validationErrors.lawSchoolGraduationYear}
              />
            </Grid>

            {/* Phone Numbers */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                Phone Numbers *
              </Typography>
            </Grid>
            {phoneNumbers.map((phone, idx) => (
              <Grid item xs={12} key={idx}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      select
                      label="Type"
                      value={phone.type}
                      onChange={e => handlePhoneChange(idx, 'type', e.target.value)}
                      fullWidth
                    >
                      {phoneTypes.map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={idx === 0 ? 'Primary Number' : 'Secondary Number'}
                      value={phone.number}
                      onChange={e => handlePhoneChange(idx, 'number', e.target.value)}
                      onBlur={() => handlePhoneBlur(idx)}
                      fullWidth
                      type="tel"
                      required={idx === 0}
                      inputProps={{ maxLength: 20 }}
                      error={!!validationErrors.phoneNumbers}
                      helperText={
                        validationErrors.phoneNumbers && idx === 0
                          ? validationErrors.phoneNumbers
                          : idx === 1 ? 'Optional - Format: (xxx) xxx-xxxx' : 'Format: (xxx) xxx-xxxx'
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
            ))}

            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, mt: 2 }}>
                Please feel free to list any other information, credentials, accomplishments and ratings you would like us to make available to potential clients
              </Typography>
              <TextField
                label="Comments"
                name="comments"
                value={form.comments}
                onChange={handleChange}
                fullWidth
                multiline
                minRows={4}
                InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: 4000 }}
                helperText="Optional"
              />
            </Grid>

            {/* Profile Photo Upload */}
            <Grid item xs={12} sx={{ order: { xs: 21, sm: 0 } }}>
              <Divider
                sx={{
                  my: { xs: 2, sm: 3 },
                  borderColor: 'divider',
                }}
              />
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Profile Photo (Optional)
                </Typography>
                <PhotoUpload
                  currentPhotoUrl={profilePhotoUrl}
                  onPhotoChange={setProfilePhotoUrl}
                  onPhotoFile={setProfilePhotoFile}
                  onError={(error) => setSubmitError(error)}
                  disabled={submitting}
                />
              </Box>
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={2}>
            {/* Primary Office Location */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                Primary Office Location
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Address Line 1" 
                name="line1" 
                value={primaryOffice.line1} 
                onChange={handlePrimaryOfficeChange} 
                fullWidth 
                required 
                error={!!validationErrors.primaryOffice?.line1}
                helperText={validationErrors.primaryOffice?.line1}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Address Line 2" 
                name="line2" 
                value={primaryOffice.line2} 
                onChange={handlePrimaryOfficeChange} 
                fullWidth 
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField 
                label="City" 
                name="city" 
                value={primaryOffice.city} 
                onChange={handlePrimaryOfficeChange} 
                fullWidth 
                required 
                error={!!validationErrors.primaryOffice?.city}
                helperText={validationErrors.primaryOffice?.city}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField 
                select 
                label="State" 
                name="state" 
                value={primaryOffice.state} 
                onChange={handlePrimaryOfficeChange} 
                fullWidth 
                required 
                error={!!validationErrors.primaryOffice?.state}
                helperText={validationErrors.primaryOffice?.state}
              >
                {US_STATES.map(state => (
                  <MenuItem key={state} value={state}>{state}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField 
                label="Zip Code" 
                name="zipCode" 
                value={primaryOffice.zipCode} 
                onChange={handlePrimaryOfficeChange} 
                fullWidth 
                required 
                error={!!validationErrors.primaryOffice?.zipCode}
                helperText={validationErrors.primaryOffice?.zipCode}
              />
            </Grid>

            {/* Other Office Locations */}
            <Grid item xs={12}>
              <TextField
                label="Other Office Location(s)"
                name="otherOffices"
                value={otherOffices}
                onChange={e => setOtherOffices(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                sx={{ mt: 2 }}
              />
            </Grid>

            {/* Professional Liability Insurance */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                Do you maintain professional liability insurance?
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4} id="reg-step2-liability-insurance">
              <TextField
                select
                label="Liability insurance?"
                value={hasProfessionalLiabilityInsurance}
                onChange={e => {
                  setHasProfessionalLiabilityInsurance(e.target.value as '' | 'yes' | 'no');
                  if (validationErrors.hasProfessionalLiabilityInsurance) {
                    setValidationErrors({ ...validationErrors, hasProfessionalLiabilityInsurance: undefined });
                  }
                }}
                fullWidth
                required
                error={!!validationErrors.hasProfessionalLiabilityInsurance}
                helperText={validationErrors.hasProfessionalLiabilityInsurance}
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </TextField>
            </Grid>

            {/* State(s) Licensed to Practice */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                State(s) Licensed to Practice
              </Typography>
            </Grid>
            {stateLicenses.map((lic, idx) => (
              <React.Fragment key={idx}>
                <Grid item xs={12} sm={3}>
                  <TextField
                    select
                    label="State"
                    name="state"
                    value={lic.state}
                    onChange={e => handleStateLicenseChange(idx, 'state', e.target.value)}
                    fullWidth
                    required
                  >
                    {US_STATES.map(state => (
                      <MenuItem key={state} value={state}>{state}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="State Bar ID No."
                    name="barIdNo"
                    value={lic.barIdNo}
                    onChange={e => handleStateLicenseChange(idx, 'barIdNo', e.target.value)}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    select
                    label="Active?"
                    name="isActive"
                    value={lic.isActive}
                    onChange={e => handleStateLicenseChange(idx, 'isActive', e.target.value)}
                    fullWidth
                    required
                  >
                    <MenuItem value="">
                      <em>Select</em>
                    </MenuItem>
                    <MenuItem value="yes">Yes</MenuItem>
                    <MenuItem value="no">No</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    select
                    label="Good Standing?"
                    name="inGoodStanding"
                    value={lic.inGoodStanding}
                    onChange={e => handleStateLicenseChange(idx, 'inGoodStanding', e.target.value)}
                    fullWidth
                    required
                  >
                    <MenuItem value="">
                      <em>Select</em>
                    </MenuItem>
                    <MenuItem value="yes">Yes</MenuItem>
                    <MenuItem value="no">No</MenuItem>
                  </TextField>
                </Grid>
                {lic.inGoodStanding === 'no' && (
                  <Grid item xs={12} sm={10}>
                    <TextField
                      label="Status Note"
                      name="statusNote"
                      value={lic.statusNote}
                      onChange={e => handleStateLicenseChange(idx, 'statusNote', e.target.value)}
                      fullWidth
                      multiline
                      minRows={1}
                      maxRows={3}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={2}>
                  {stateLicenses.length > 1 && (
                    <Button color="error" onClick={() => removeStateLicense(idx)} sx={{ mt: 1 }}>
                      Remove
                    </Button>
                  )}
                </Grid>
              </React.Fragment>
            ))}
            {validationErrors.stateLicenses && (
              <Grid item xs={12}>
                <FormHelperText error>{validationErrors.stateLicenses}</FormHelperText>
              </Grid>
            )}
            <Grid item xs={12}>
              <Button onClick={addStateLicense} sx={{ mt: 1 }}>
                Add Another State License
              </Button>
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={2}>
            {/* Law Practice Areas of Concentration */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 2 }}>
                Law Practice Areas of Concentration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select practice areas and specify what percentage of your time you spend on each. Percentages must total 100%.
              </Typography>
            </Grid>

            {loadingPracticeAreas ? (
              <Grid item xs={12}>
                <Typography>Loading practice areas...</Typography>
              </Grid>
            ) : (
              <>
                {/* Add Practice Area - 2 Level Selection */}
                <Grid item xs={12} sm={4}>
              <TextField
                select
                    label="Category"
                    value={selectedCategoryForAdd}
                    onChange={e => {
                      const categoryId = e.target.value;
                      setSelectedCategoryForAdd(categoryId);
                      // Reset Other-category rows when switching categories
                      setOtherCategoryRows([{ text: '', percentage: '', error: '' }]);
                    }}
                fullWidth
                  >
                    <MenuItem value="">
                      <em>Select Category</em>
                    </MenuItem>
                    {practiceAreaCategories
                      .map(category => (
                        <MenuItem key={category.id} value={category.id}>
                          {category.name}
                        </MenuItem>
                ))}
              </TextField>
            </Grid>

                {/* Subcategory Checkbox List with Percentage Fields OR Other Category Text Field */}
                {selectedCategoryForAdd && (() => {
                  const category = practiceAreaCategories.find(c => c.id === selectedCategoryForAdd);
                  if (!category) {
                    return null;
                  }
                  
                  // Handle "Other" category - show text fields (one per entry) instead of checkboxes
                  if (category.name === 'Other' || category.subcategories.length === 0) {
                    const commitRow = (rowIdx: number) => {
                      const row = otherCategoryRows[rowIdx];
                      const text = row.text.trim();
                      const pct = parseInt(row.percentage, 10);
                      if (!text || isNaN(pct) || pct < 1 || pct > 100) return;
                      // Replace the matching entry in practiceAreas (keyed by rowIdx sentinel)
                      // We store a rowTag in otherArea: "rowN::text" to distinguish multiple entries,
                      // but expose only the text portion for display/submit.
                      const rowTag = `__row${rowIdx}__`;
                      setPracticeAreas(prev => {
                        const withoutRow = prev.filter(pa => !(pa.categoryId === category.id && (pa as any)._rowTag === rowTag));
                        return [...withoutRow, {
                          categoryId: category.id,
                          categoryName: category.name,
                          subcategoryId: null,
                          otherArea: text,
                          percentage: pct,
                          _rowTag: rowTag,
                        } as any];
                      });
                    };

                    const updateRow = (rowIdx: number, field: 'text' | 'percentage', value: string) => {
                      setOtherCategoryRows(prev => prev.map((r, i) => i === rowIdx ? { ...r, [field]: value, error: '' } : r));
                    };

                    const removeRow = (rowIdx: number) => {
                      const rowTag = `__row${rowIdx}__`;
                      setPracticeAreas(prev => prev.filter(pa => !((pa as any)._rowTag === rowTag)));
                      setOtherCategoryRows(prev => {
                        const next = prev.filter((_, i) => i !== rowIdx);
                        return next.length === 0 ? [{ text: '', percentage: '', error: '' }] : next;
                      });
                    };

                    return (
                      <Grid item xs={12} sm={8}>
                        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                            Enter Custom Practice Area(s) and Percentage:
                          </Typography>
                          {otherCategoryRows.map((row, rowIdx) => (
                            <Grid container spacing={2} key={rowIdx} sx={{ mb: rowIdx < otherCategoryRows.length - 1 ? 2 : 0 }}>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  label="Practice Area Name"
                                  value={row.text}
                                  onChange={e => updateRow(rowIdx, 'text', e.target.value)}
                                  onBlur={() => commitRow(rowIdx)}
                                  fullWidth
                                  required
                                  error={!!row.error}
                                  helperText={row.error || 'Enter a custom practice area name'}
                                />
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <TextField
                                  type="number"
                                  label="Percentage (%)"
                                  value={row.percentage}
                                  onChange={e => {
                                    const v = e.target.value;
                                    if (v === '' || /^\d+$/.test(v)) updateRow(rowIdx, 'percentage', v);
                                  }}
                                  onBlur={e => {
                                    const v = parseInt(e.target.value, 10);
                                    if (isNaN(v) || v < 1 || v > 100) {
                                      setOtherCategoryRows(prev => prev.map((r, i) => i === rowIdx ? { ...r, error: 'Must be a whole number between 1 and 100' } : r));
                                      return;
                                    }
                                    commitRow(rowIdx);
                                  }}
                                  onKeyDown={handleEnterAsBlur}
                                  fullWidth
                                  required
                                  inputProps={{ min: 1, max: 100, step: 1 }}
                                  error={!!row.error}
                                  helperText={row.error}
                                />
                              </Grid>
                              <Grid item xs={12} sm={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                {(otherCategoryRows.length > 1) && (
                                  <Button color="error" onClick={() => removeRow(rowIdx)} size="small">
                                    Remove
                                  </Button>
                                )}
                              </Grid>
                            </Grid>
                          ))}
                          <Button
                            size="small"
                            sx={{ mt: 2 }}
                            onClick={() => setOtherCategoryRows(prev => [...prev, { text: '', percentage: '', error: '' }])}
                          >
                            + Add Another
                          </Button>
                        </Box>
                      </Grid>
                    );
                  }
                  
                  // Regular category with subcategories
                  return (
                    <Grid item xs={12} sm={8}>
                      <Box sx={{ 
                        border: '1px solid', 
                        borderColor: 'divider', 
                        borderRadius: 1, 
                        p: 2,
                        maxHeight: 300,
                        overflow: 'auto'
                      }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Select Subcategories and Enter Percentages:
                        </Typography>
                        <Grid container spacing={2}>
                          {category.subcategories.map(subcategory => {
                            const subcategoryKey = `${category.id}-${subcategory.id}`;
                            const isInList = practiceAreas.some(pa => 
                              pa.categoryId === category.id && pa.subcategoryId === subcategory.id
                            );
                            const isChecked = checkedSubcategories.has(subcategoryKey) || isInList;
                            // Get percentage value: from state, or from list if in list, or 0
                            const percentageValue = subcategoryPercentages[subcategoryKey] ?? (isInList ? practiceAreas.find(pa => pa.categoryId === category.id && pa.subcategoryId === subcategory.id)?.percentage ?? 0 : 0);
                            const percentageError = subcategoryPercentageErrors[subcategoryKey];
                            const customTextValue = subcategoryCustomTexts[subcategoryKey] ?? (isInList ? practiceAreas.find(pa => pa.categoryId === category.id && pa.subcategoryId === subcategory.id)?.subCategoryCustomText ?? '' : '');
                            const customTextError = subcategoryCustomTextErrors[subcategoryKey];
                            const requiresCustomText = subcategory.requiresCustomText || false;
                            const customTextRequired = subcategory.customTextRequired || false;
                            
                            return (
                              <Grid item xs={12} key={subcategory.id}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                                  <Checkbox
                                    checked={isChecked}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        // Enable the percentage field - add to checked set
                                        setCheckedSubcategories(prev => new Set(prev).add(subcategoryKey));
                                        // Initialize percentage to 0 if not set
                                        if (subcategoryPercentages[subcategoryKey] === undefined) {
                                          setSubcategoryPercentages(prev => ({
                                            ...prev,
                                            [subcategoryKey]: 0
                                          }));
                                        }
                                        if (requiresCustomText && !scrolledCustomTextKeysRef.current.has(subcategoryKey)) {
                                          pendingOneTimeScrollKeyRef.current = subcategoryKey;
                                        }
                                        const storedPct = subcategoryPercentages[subcategoryKey] ?? percentageValue;
                                        if (storedPct >= 1 && storedPct <= 100) {
                                          const customText = subcategoryCustomTexts[subcategoryKey] ?? customTextValue;
                                          setPracticeAreas(prev => {
                                            if (prev.some(pa => pa.categoryId === category.id && pa.subcategoryId === subcategory.id)) {
                                              return prev;
                                            }
                                            return [...prev, {
                                              categoryId: category.id,
                                              categoryName: category.name,
                                              subcategoryId: subcategory.id,
                                              subcategoryName: subcategory.name,
                                              subCategoryCustomText: customText || undefined,
                                              percentage: storedPct
                                            }];
                                          });
                                        }
                                      } else {
                                        // Remove from practice areas
                                        setPracticeAreas(prev => prev.filter(pa => 
                                          !(pa.categoryId === category.id && pa.subcategoryId === subcategory.id)
                                        ));
                                        // Remove from checked set
                                        setCheckedSubcategories(prev => {
                                          const newSet = new Set(prev);
                                          newSet.delete(subcategoryKey);
                                          return newSet;
                                        });
                                        scrolledCustomTextKeysRef.current.delete(subcategoryKey);
                                        // Keep the percentage value but disable the field
                                        // Clear validation error when removing
                                        if (validationErrors.practiceAreaPercentages) {
                                          setValidationErrors({ ...validationErrors, practiceAreaPercentages: undefined });
                                        }
                                      }
                                    }}
                                  />
                                  <Typography sx={{ minWidth: { xs: 0, sm: 200 }, flex: '1 1 auto' }}>
                                    {subcategory.name}
                                  </Typography>
                                  <TextField
                                    type="number"
                                    label="Percentage"
                                    value={percentageValue || ''}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      // Only allow numbers
                                      if (value === '' || /^\d+$/.test(value)) {
                                        const numValue = value === '' ? 0 : parseInt(value, 10);
                                        setSubcategoryPercentages(prev => ({
                                          ...prev,
                                          [subcategoryKey]: numValue
                                        }));
                                        // Clear error when user types
                                        if (subcategoryPercentageErrors[subcategoryKey]) {
                                          setSubcategoryPercentageErrors(prev => {
                                            const newErrors = { ...prev };
                                            delete newErrors[subcategoryKey];
                                            return newErrors;
                                          });
                                        }
                                      }
                                    }}
                                    onBlur={(e) => {
                                      const value = parseInt(e.target.value, 10);
                                      // Validate: must be whole number between 1-100
                                      if (isNaN(value) || value < 1 || value > 100) {
                                        setSubcategoryPercentageErrors(prev => ({
                                          ...prev,
                                          [subcategoryKey]: 'Must be a whole number between 1 and 100'
                                        }));
                                        return;
                                      }
                                      
                                      // Clear any error
                                      setSubcategoryPercentageErrors(prev => {
                                        const newErrors = { ...prev };
                                        delete newErrors[subcategoryKey];
                                        return newErrors;
                                      });
                                      
                                      // Check if already in list
                                      const exists = practiceAreas.find(pa => 
                                        pa.categoryId === category.id && pa.subcategoryId === subcategory.id
                                      );
                                      
                                      const customText = subcategoryCustomTexts[subcategoryKey] || '';
                                      if (!exists) {
                                        // Add to practice areas
                                        setPracticeAreas([...practiceAreas, {
                                          categoryId: category.id,
                                          categoryName: category.name,
                                          subcategoryId: subcategory.id,
                                          subcategoryName: subcategory.name,
                                          subCategoryCustomText: customText || undefined,
                                          percentage: value
                                        }]);
                                        // Ensure checkbox stays checked
                                        setCheckedSubcategories(prev => new Set(prev).add(subcategoryKey));
                                      } else {
                                        // Update existing entry
                                        setPracticeAreas(prev => prev.map(pa => 
                                          pa.categoryId === category.id && pa.subcategoryId === subcategory.id
                                            ? { ...pa, percentage: value, subCategoryCustomText: customText || undefined }
                                            : pa
                                        ));
                                      }
                                      
                                      // Clear validation error when adding/updating
                                      if (validationErrors.practiceAreaPercentages) {
                                        setValidationErrors({ ...validationErrors, practiceAreaPercentages: undefined });
                                      }
                                    }}
                                    onKeyDown={handleEnterAsBlur}
                                    disabled={!isChecked}
                                    error={!!percentageError}
                                    helperText={percentageError}
                                    inputProps={{ min: 1, max: 100, step: 1 }}
                                    sx={{ width: { xs: '100%', sm: 150 }, minWidth: { xs: 0, sm: 150 } }}
                                  />
                                  </Box>
                                  {requiresCustomText && isChecked && (
                                    <Box 
                                      sx={{ ml: { xs: 0, sm: 5 }, mt: { xs: 1, sm: 0 } }}
                                      ref={(el: HTMLDivElement | null) => {
                                        customTextFieldRefs.current[subcategoryKey] = el;
                                      }}
                                    >
                                      <TextField
                                        label="Additional Details"
                                        value={customTextValue}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          setSubcategoryCustomTexts(prev => ({
                                            ...prev,
                                            [subcategoryKey]: value
                                          }));
                                          // Clear error when user types
                                          if (subcategoryCustomTextErrors[subcategoryKey]) {
                                            setSubcategoryCustomTextErrors(prev => {
                                              const newErrors = { ...prev };
                                              delete newErrors[subcategoryKey];
                                              return newErrors;
                                            });
                                          }
                                          // Update practice areas
                                          const exists = practiceAreas.find(pa => 
                                            pa.categoryId === category.id && pa.subcategoryId === subcategory.id
                                          );
                                          if (exists) {
                                            setPracticeAreas(prev => prev.map(pa => 
                                              pa.categoryId === category.id && pa.subcategoryId === subcategory.id
                                                ? { ...pa, subCategoryCustomText: value }
                                                : pa
                                            ));
                                          }
                                        }}
                                        onBlur={(e) => {
                                          const value = e.target.value.trim();
                                          // Validate if required
                                          if (customTextRequired && !value) {
                                            setSubcategoryCustomTextErrors(prev => ({
                                              ...prev,
                                              [subcategoryKey]: 'This field is required'
                                            }));
                                            return;
                                          }
                                          // Clear error
                                          setSubcategoryCustomTextErrors(prev => {
                                            const newErrors = { ...prev };
                                            delete newErrors[subcategoryKey];
                                            return newErrors;
                                          });
                                          // Update practice areas
                                          const exists = practiceAreas.find(pa => 
                                            pa.categoryId === category.id && pa.subcategoryId === subcategory.id
                                          );
                                          if (exists) {
                                            setPracticeAreas(prev => prev.map(pa => 
                                              pa.categoryId === category.id && pa.subcategoryId === subcategory.id
                                                ? { ...pa, subCategoryCustomText: value }
                                                : pa
                                            ));
                                          } else if (value && percentageValue > 0) {
                                            // Add to practice areas if both percentage and custom text are set
                                            setPracticeAreas([...practiceAreas, {
                                              categoryId: category.id,
                                              categoryName: category.name,
                                              subcategoryId: subcategory.id,
                                              subcategoryName: subcategory.name,
                                              subCategoryCustomText: value,
                                              percentage: percentageValue
                                            }]);
                                          }
                                        }}
                                        fullWidth
                                        required={customTextRequired}
                                        error={!!customTextError}
                                        helperText={customTextError || (customTextRequired ? 'Required' : 'Optional')}
                                        sx={{ maxWidth: { xs: '100%', sm: 400 } }}
                                      />
                                    </Box>
                                  )}
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    </Grid>
                  );
                })()}

                {/* Total Percentage Display */}
              <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: practiceAreas.reduce((sum, pa) => sum + (pa.percentage || 0), 0) === 100 
                          ? 'success.main' 
                          : 'error.main',
                        fontWeight: 'bold'
                      }}
                    >
                      Total: {practiceAreas.reduce((sum, pa) => sum + (pa.percentage || 0), 0)}%
                    </Typography>
                  </Box>
                </Grid>

                {/* Selected Practice Areas with Percentages */}
                {practiceAreas.map((pa, idx) => {
                  // Generate unique key - handle both regular and "Other" category
                  const practiceAreaKey = pa.otherArea 
                    ? `${pa.categoryId}-other-${idx}`
                    : `${pa.categoryId}-${pa.subcategoryId}`;
                  
                  // Display name - show otherArea if it exists, otherwise show subcategory
                  // Append custom text if it exists
                  let displayName = pa.otherArea 
                    ? `${pa.categoryName}: ${pa.otherArea}`
                    : `${pa.categoryName}: ${pa.subcategoryName}`;
                  if (pa.subCategoryCustomText) {
                    displayName += ` - ${pa.subCategoryCustomText}`;
                  }
                  
                  return (
                  <Grid item xs={12} key={practiceAreaKey}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                <TextField
                        label="Practice Area"
                        value={displayName}
                        sx={{ flex: '1 1 200px', minWidth: 0 }}
                        InputProps={{ readOnly: true }}
                      />
                      <TextField
                        label="Percentage (%)"
                        type="number"
                        value={pa.percentage || ''}
                        onChange={e => {
                          const value = e.target.value;
                          // Only allow numbers
                          if (value === '' || /^\d+$/.test(value)) {
                            const numValue = value === '' ? 0 : parseInt(value, 10);
                            if (numValue >= 0 && numValue <= 100) {
                              setPracticeAreas(prev => prev.map((p, i) => 
                                i === idx ? { ...p, percentage: numValue } : p
                              ));
                              // Update the subcategoryPercentages state too
                              const subcategoryKey = `${pa.categoryId}-${pa.subcategoryId}`;
                              setSubcategoryPercentages(prev => ({
                                ...prev,
                                [subcategoryKey]: numValue
                              }));
                              // Clear validation error when user types
                              if (validationErrors.practiceAreaPercentages) {
                                setValidationErrors({ ...validationErrors, practiceAreaPercentages: undefined });
                              }
                            }
                          }
                        }}
                        onKeyDown={handleEnterAsBlur}
                        required
                        inputProps={{ min: 1, max: 100, step: 1 }}
                        error={!!validationErrors.practiceAreaPercentages && (!pa.percentage || pa.percentage < 1 || pa.percentage > 100)}
                        helperText={
                          !pa.percentage || pa.percentage < 1 || pa.percentage > 100
                            ? 'Must be between 1% and 100%'
                            : ''
                        }
                        sx={{ width: { xs: '100%', sm: 150 }, minWidth: { xs: 0, sm: 150 } }}
                      />
                      <Button
                        color="error"
                        onClick={() => {
                          // Remove from practice areas
                          setPracticeAreas(prev => prev.filter((_, i) => i !== idx));
                          
                          // If it's a regular subcategory, remove from checked set
                          if (pa.subcategoryId) {
                            const subcategoryKey = `${pa.categoryId}-${pa.subcategoryId}`;
                            setCheckedSubcategories(prev => {
                              const newSet = new Set(prev);
                              newSet.delete(subcategoryKey);
                              return newSet;
                            });
                          } else if (pa.otherArea) {
                            // Remove the matching row from otherCategoryRows by its _rowTag
                            const rowTag = (pa as any)._rowTag;
                            if (rowTag) {
                              const rowIdx = parseInt(rowTag.replace('__row', '').replace('__', ''), 10);
                              setOtherCategoryRows(prev => {
                                const next = prev.filter((_, i) => i !== rowIdx);
                                return next.length === 0 ? [{ text: '', percentage: '', error: '' }] : next;
                              });
                            }
                          }
                          
                          // Clear validation error
                          if (validationErrors.practiceAreaPercentages) {
                            setValidationErrors({ ...validationErrors, practiceAreaPercentages: undefined });
                          }
                        }}
                      >
                        Remove
                      </Button>
                    </Box>
                  </Grid>
                );
                })}

                {/* Validation Error Messages */}
                {validationErrors.practiceAreas && (
                  <Grid item xs={12}>
                    <FormHelperText error>{validationErrors.practiceAreas}</FormHelperText>
              </Grid>
                )}
                {validationErrors.practiceAreaPercentages && (
                  <Grid item xs={12}>
                    <FormHelperText error>{validationErrors.practiceAreaPercentages}</FormHelperText>
                  </Grid>
                )}
              </>
            )}

            {/* Disciplinary Actions */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                Disciplinary Actions
              </Typography>
            </Grid>
            <Grid item xs={12} sm={8} id="reg-step2-disciplinary-select">
              <TextField
                select
                label="Any past violations, criminal convictions, or pending disciplinary or criminal matters?"
                value={disciplinary.hasAction}
                onChange={e => {
                  const v = e.target.value as '' | 'yes' | 'no';
                  setDisciplinary({
                    hasAction: v,
                    narrative: v === 'no' ? '' : disciplinary.narrative,
                  });
                  setValidationErrors(prev => ({
                    ...prev,
                    disciplinary: undefined,
                    ...(v !== 'yes' ? { disciplinaryNarrative: undefined } : {}),
                  }));
                }}
                fullWidth
                required
                error={!!validationErrors.disciplinary}
                helperText={validationErrors.disciplinary}
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </TextField>
            </Grid>
            {disciplinary.hasAction === 'yes' && (
              <Grid item xs={12} sm={8} id="reg-step2-disciplinary-narrative">
                <TextField
                  label="Please elaborate"
                  value={disciplinary.narrative}
                  onChange={e => {
                    setDisciplinary({ ...disciplinary, narrative: e.target.value });
                    if (validationErrors.disciplinaryNarrative) {
                      setValidationErrors({ ...validationErrors, disciplinaryNarrative: undefined });
                    }
                  }}
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={4}
                  required
                  error={!!validationErrors.disciplinaryNarrative}
                  helperText={validationErrors.disciplinaryNarrative}
                />
              </Grid>
            )}

            {/* Standard Hourly Rate and Other Fee Options */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                2026 Standard Hourly Rate & Other Fee Options
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                These rates will not be listed in the legal directory, but dollar signs (ranging from $ to $$$$$) will indicate comparative fee ranges.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Standard Hourly Rate ($)"
                name="hourlyRate"
                value={feeInfo.hourlyRate}
                onChange={e => {
                  const value = e.target.value;
                  // Only allow whole numbers
                  if (value === '' || /^\d+$/.test(value)) {
                    setFeeInfo({ ...feeInfo, hourlyRate: value });
                  }
                }}
                type="number"
                fullWidth
                inputProps={{ min: 0, step: 1 }}
                helperText="Must be a whole number"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1, fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                We encourage lawyers to offer discounted rates to clients found through ATJustice. If you are willing to offer such a discounted rate, please feel free to list it here. These rates will not be listed in the legal directory, but will be taken into account in dollar symbols ($ - $$$$$) and availability of those discounts will be separately noted.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Discounted Hourly Rate ($)"
                name="discountedHourlyRate"
                value={feeInfo.discountedHourlyRate}
                onChange={e => {
                  const value = e.target.value;
                  // Only allow whole numbers
                  if (value === '' || /^\d+$/.test(value)) {
                    setFeeInfo({ ...feeInfo, discountedHourlyRate: value });
                    if (validationErrors.discountedHourlyRate) {
                      setValidationErrors({ ...validationErrors, discountedHourlyRate: undefined });
                    }
                  }
                }}
                type="number"
                fullWidth
                inputProps={{ min: 0, step: 1 }}
                error={!!validationErrors.discountedHourlyRate}
                helperText={validationErrors.discountedHourlyRate || 'Must be a whole number'}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1, fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                Please list any other fee options here, such as contingent fee percentages or fixed price offerings.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Other Fee Options"
                name="otherFees"
                value={feeInfo.otherFees}
                onChange={e => setFeeInfo({ ...feeInfo, otherFees: e.target.value })}
                fullWidth
                multiline
                minRows={1}
                maxRows={3}
              />
            </Grid>

            {/* Pro Bono Opportunities */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                Would you like to be contacted about pro bono (including free services and/or reduced fee alternatives based on client economic circumstances) opportunities relevant to your practice area(s) and locations?
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4} id="reg-step2-pro-bono">
              <TextField
                select
                label="Contact about pro bono?"
                value={proBonoInterest}
                onChange={e => {
                  setProBonoInterest(e.target.value as '' | 'yes' | 'no');
                  if (validationErrors.proBono) {
                    setValidationErrors({ ...validationErrors, proBono: undefined });
                  }
                }}
                fullWidth
                required
                error={!!validationErrors.proBono}
                helperText={validationErrors.proBono}
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </TextField>
            </Grid>

            {/* Free Initial Consultation */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                Do you wish to offer a free initial 30-minute consultation to clients found through ATJustice?
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4} id="reg-step3-free-consultation">
              <TextField
                select
                label="Free initial consultation?"
                value={offersFreeConsultation}
                onChange={e => {
                  setOffersFreeConsultation(e.target.value as '' | 'yes' | 'no');
                  if (validationErrors.offersFreeConsultation) {
                    setValidationErrors({ ...validationErrors, offersFreeConsultation: undefined });
                  }
                }}
                fullWidth
                required
                error={!!validationErrors.offersFreeConsultation}
                helperText={validationErrors.offersFreeConsultation}
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </TextField>
            </Grid>

            {/* Terms and Conditions */}
            <LawyerTermsCheckbox checked={termsAccepted} onChange={setTermsAccepted} />
          </Grid>
        );

      default:
        return null;
    }
  };

  if (submitted) {
    return (
      <Container maxWidth="md">
        <Card sx={{ mt: 4 }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            {confirmationNumber ? (
              <>
                <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
                <Typography variant="h4" gutterBottom color="success.main">
                  Submission Successful! Thank you for submitting your registration information to ATJustice.
                </Typography>
                <Box sx={{ 
                  p: 3, 
                  borderRadius: 2, 
                  mb: 3,
                  textAlign: 'left'
                }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Verify Your Email Address
                </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    We've sent a verification email to <strong>{registrationEmail}</strong>. 
                    Please check your Inbox and click the verification link to complete your registration.
                </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                     The verification link will expire in 4 hours.
                </Typography>
                  {resendMessage && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        mb: 2,
                        color: resendMessage.type === 'success' ? 'success.main' : 'error.main',
                        fontWeight: 'bold'
                      }}
                    >
                      {resendMessage.text}
                    </Typography>
                  )}
                  <Button 
                    variant="outlined" 
                    size="medium"
                    disabled={resendingVerification}
                    onClick={async () => {
                      setResendingVerification(true);
                      setResendMessage(null);
                      try {
                        const response = await axios.post('/api/lawyers/resend-verification', {
                          email: registrationEmail
                        });
                        setResendMessage({ 
                          type: 'success', 
                          text: response.data.message || 'Verification email sent successfully!' 
                        });
                      } catch (err: any) {
                        setResendMessage({ 
                          type: 'error', 
                          text: err?.response?.data?.message || err?.response?.data?.error || 'Failed to resend verification email. Please try again later.' 
                        });
                      } finally {
                        setResendingVerification(false);
                      }
                    }}
                    sx={{ mr: 2 }}
                  >
                    {resendingVerification ? 'Sending...' : 'Resend Verification Email'}
                  </Button>
                </Box>
                {/*} <Button 
                  variant="contained" 
                  size="large"
                  onClick={() => {
                    setSubmitted(false);
                    setConfirmationNumber('');
                    setRegistrationEmail('');
                    setResendMessage(null);
                    setActiveStep(0);
                    setForm({ firstName: '', middleInitial: '', lastName: '', suffix: '', email: '', firmName: '', practiceWebsiteUrl: '', linkedInUrl: '', yearsPracticingLaw: '', lawSchool: '', lawSchoolGraduationYear: '', comments: '' });
                    setPhoneNumbers([{ number: '', type: 'office' }, { number: '', type: 'cell' }]);
                    setPrimaryOffice({ line1: '', line2: '', city: '', state: '', zipCode: '' });
                    setOtherOffices('');
                    setStateLicenses([{ state: '', barIdNo: '', isActive: '', inGoodStanding: '', statusNote: '' }]);
                    setPracticeAreas([]);
                    setSelectedCategoryForAdd('');
                    setCheckedSubcategories(new Set());
                    setSubcategoryPercentages({});
                    setSubcategoryPercentageErrors({});
                    setOtherCategoryRows([{ text: '', percentage: '', error: '' }]);
                    setProfilePhotoUrl(null);
                    setProfilePhotoFile(null);
                    setDisciplinary({ hasAction: '', narrative: '' });
                    setFeeInfo({ hourlyRate: '', discountedHourlyRate: '', otherFees: '' });
                    setProBonoInterest('');
                    setValidationErrors({});
                    setTermsAccepted(false);
                  }}
                >
                  Register Another Lawyer
                </Button> */}
              </>
            ) : (
              <>
                <Typography variant="h4" gutterBottom color="error.main">
                  Registration Failed
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  {submitError || 'An error occurred during registration. Please try again.'}
                </Typography>
                <Button 
                  variant="contained" 
                  size="large"
                  onClick={() => setSubmitted(false)}
                >
                  Try Again
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 } }}>
      <Paper sx={{ p: { xs: 2, sm: 4 }, overflow: 'hidden' }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' }, 
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'center' }, 
          gap: 2,
          mb: 2 
        }}>
          <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            Lawyer Registration
          </Typography>
          <Button
            component={Link}
            to="/register/edit-request"
            variant="text"
            color="primary"
            sx={{ textTransform: 'none', alignSelf: { xs: 'flex-start', sm: 'center' }, whiteSpace: 'normal', textAlign: 'left' }}
          >
            Already Registered? You can edit your registration here
          </Button>
        </Box>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4, px: { xs: 0, sm: 2 } }}>
          Complete the form below to be included in our directory listing for potential clients. All fields marked with * are required.
          Estimated 10 minutes to complete.
        </Typography>

        {/* Stepper - alternativeLabel and responsive for mobile */}
        <Stepper activeStep={activeStep} sx={{ mb: 4, overflow: 'auto' }} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel sx={{ '& .MuiStepLabel-label': { fontSize: { xs: '0.75rem', sm: '0.875rem' } } }}>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Form Content */}
        <form onSubmit={(e) => e.preventDefault()}>
          {renderStepContent(activeStep)}

          {/* Navigation Buttons - stack on very small screens */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column-reverse', sm: 'row' }, 
            justifyContent: 'space-between', 
            alignItems: 'stretch',
            gap: 2,
            mt: 4 
          }}>
            <Button
              onClick={handleBack}
              startIcon={activeStep === 0 ? <Close /> : <ArrowBack />}
              size="large"
              fullWidth={false}
              sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
            >
              {activeStep === 0 ? 'Cancel' : 'Previous'}
            </Button>
            <Box sx={{ alignSelf: { xs: 'stretch', sm: 'flex-end' } }}>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={submitting || !termsAccepted}
                  startIcon={<Save />}
                  size="large"
                  fullWidth
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  {submitting ? 'Submitting...' : 'Submit Registration'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<ArrowForward />}
                  size="large"
                  fullWidth
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

// Registration type choice screen
type RegistrationType = null | 'directory' | 'referral' | 'both';

const RegistrationTypeChoice: React.FC<{ onSelect: (type: RegistrationType) => void }> = ({ onSelect }) => {
  const choices: { type: RegistrationType; title: string; description: string }[] = [
    {
      type: 'directory',
      title: 'Join the Registry to be Included in the Directory for Potential Clients',
      description: 'Enroll in the ATJustice attorney registry for clients to receive your information',
    },
    {
      type: 'referral',
      title: 'Join the Registry to Help Find Other Lawyers',
      description: "Register to find lawyers in a particular practice area and/or jurisdiction where you and your colleagues don't currently practice",
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 } }}>
      <Paper sx={{ p: { xs: 2, sm: 4 } }}>
        <Typography variant="h4" component="h1" align="center" sx={{ mb: 2, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Lawyer Registration
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
          How would you like to register with ATJustice?
        </Typography>
        <Grid container spacing={3} justifyContent="center">
          {choices.map(choice => (
            <Grid item xs={12} sm={6} key={choice.type as string}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: 4, borderColor: 'primary.main' },
                  display: 'flex',
                  flexDirection: 'column',
                }}
                onClick={() => onSelect(choice.type)}
              >
                <CardContent sx={{ flex: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    {choice.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {choice.description}
                  </Typography>
                </CardContent>
                <Box sx={{ p: 2, pt: 0 }}>
                  <Button variant="contained" fullWidth onClick={() => onSelect(choice.type)}>
                    Select
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Box sx={{ mt: 4, textAlign: 'center', borderTop: 1, borderColor: 'divider', pt: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Already registered with ATJustice?
          </Typography>
          <Button
            component={Link}
            to="/register/edit-request"
            variant="outlined"
            color="primary"
            sx={{ textTransform: 'none' }}
          >
            Access My Account
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

// Wrapper component with reCAPTCHA provider
const LawyerRegistration: React.FC = () => {
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
  const [searchParams] = useSearchParams();
  const modeParam = searchParams.get('mode');

  const [registrationType, setRegistrationType] = useState<RegistrationType>(
    modeParam === 'referral' ? 'referral' : modeParam === 'directory' ? 'directory' : null
  );

  if (!recaptchaSiteKey) {
    console.warn('REACT_APP_RECAPTCHA_SITE_KEY is not set. reCAPTCHA will not work.');
  }

  if (registrationType === null) {
    return <RegistrationTypeChoice onSelect={setRegistrationType} />;
  }

  if (registrationType === 'referral') {
    return <ReferralOnlyRegistrationForm onBackToChoice={() => setRegistrationType(null)} />;
  }

  // 'directory' or 'both' — show the full stepper form
  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={recaptchaSiteKey}
      scriptProps={{
        async: false,
        defer: false,
        appendTo: 'head',
        nonce: undefined,
      }}
    >
      <LawyerRegistrationForm
        registrationType={registrationType}
        onBackToChoice={() => setRegistrationType(null)}
      />
    </GoogleReCaptchaProvider>
  );
};

export default LawyerRegistration; 